// The review queue: decisions only Brett can make — possible duplicate contacts, look-alike
// companies, and practice-import conflicts. Merges run as one database transaction.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { ContactPicker, OrgPicker } from '../components/Pickers';
import { Button, Card, Empty, ErrorBox, Modal, PageHeader, Pill, Spinner } from '../components/ui';
import { personName, relative } from '../lib/format';
import { splitPersonName } from '../lib/import/normalize';
import { supabase, unwrap } from '../lib/supabase';

type Item = Awaited<ReturnType<typeof load>>[number];
async function load() {
  return unwrap(
    await supabase
      .from('review_queue')
      .select('id, kind, payload, created_at, candidate_org_id, candidate_contact_id, organizations(id, name), contacts(id, first_name, last_name, display_name)')
      .eq('status', 'pending')
      .order('created_at'),
  );
}

const KIND_LABEL: Record<string, string> = {
  contact_review: 'Contact to check',
  similar_company: 'Possible same company',
  practice_match: 'Practice import conflict',
};

export function Review() {
  const qc = useQueryClient();
  const { data = [], isLoading, error } = useQuery({ queryKey: ['review'], queryFn: load });
  const [merging, setMerging] = useState<Item | null>(null);

  const resolve = useMutation({
    mutationFn: async ({ item, status }: { item: Item; status: 'dismissed' | 'merged' | 'created_new' }) => {
      unwrap(await supabase.from('review_queue').update({ status, resolved_at: new Date().toISOString() }).eq('id', item.id));
      if (item.candidate_contact_id) unwrap(await supabase.from('contacts').update({ needs_review: false, review_reason: null }).eq('id', item.candidate_contact_id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['review'] });
      qc.invalidateQueries({ queryKey: ['nav-counts'] });
    },
  });

  if (isLoading) return <Spinner />;
  return (
    <div>
      <PageHeader title="Review" subtitle="Imports never merge anything on their own. These are the calls only you can make." />
      <SuggestedContacts />
      <ErrorBox error={error ?? resolve.error} />
      {data.length === 0 ? (
        <Card>
          <Empty>All clear — nothing to review.</Empty>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.map((item) => {
            const p = item.payload as Record<string, unknown>;
            return (
              <div key={item.id} className="flex flex-wrap items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill color={item.kind === 'similar_company' ? 'purple' : item.kind === 'practice_match' ? 'orange' : 'blue'}>{KIND_LABEL[item.kind] ?? item.kind}</Pill>
                    {item.contacts && (
                      <Link to={`/contacts/${item.contacts.id}`} className="font-medium text-slate-900 hover:text-indigo-700">
                        {personName(item.contacts)}
                      </Link>
                    )}
                    {item.kind === 'similar_company' && item.organizations && (
                      <Link to={`/companies/${item.organizations.id}`} className="font-medium text-slate-900 hover:text-indigo-700">
                        {item.organizations.name}
                      </Link>
                    )}
                    {item.kind === 'practice_match' && <span className="font-medium">{String((p.candidate as { dba?: string })?.dba ?? '')}</span>}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {item.kind === 'contact_review' && (p.reasons as string[] | undefined)?.join('; ')}
                    {item.kind === 'similar_company' && <>Looks like: {((p.similarTo as { name: string }[]) ?? []).map((s) => s.name).join(', ')}</>}
                    {item.kind === 'practice_match' && String(p.reason ?? '')}
                  </div>
                </div>
                <div className="flex gap-2">
                  {item.kind !== 'practice_match' && (
                    <Button size="sm" onClick={() => setMerging(item)}>
                      Merge…
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ item, status: 'dismissed' })}>
                    {item.kind === 'similar_company' ? 'Keep separate' : 'Looks fine'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal title="Merge" open={!!merging} onClose={() => setMerging(null)}>
        {merging && (
          <MergeForm
            item={merging}
            onDone={() => {
              resolve.mutate({ item: merging, status: 'merged' });
              setMerging(null);
              qc.invalidateQueries({ queryKey: ['contacts'] });
              qc.invalidateQueries({ queryKey: ['companies'] });
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function MergeForm({ item, onDone }: { item: Item; onDone: () => void }) {
  const isContact = item.kind === 'contact_review';
  const p = item.payload as { possibleDuplicateOf?: { id: string; name: string }[]; similarTo?: { id: string; name: string }[] };
  const suggestions = isContact ? (p.possibleDuplicateOf ?? []) : (p.similarTo ?? []);
  const self = isContact ? item.candidate_contact_id! : item.candidate_org_id!;
  const selfName = isContact ? (item.contacts ? personName(item.contacts) : '') : (item.organizations?.name ?? '');

  // Same-name contacts in the same file aren't in possibleDuplicateOf; find them by name.
  const { data: sameName = [] } = useQuery({
    queryKey: ['merge-candidates', self],
    enabled: isContact && suggestions.length === 0,
    queryFn: async () => {
      const me = unwrap(await supabase.from('contacts').select('display_name').eq('id', self).single());
      return unwrap(await supabase.from('contacts').select('id, first_name, last_name, display_name').eq('display_name', me.display_name ?? '').neq('id', self)).map((c) => ({
        id: c.id,
        name: personName(c),
      }));
    },
  });
  const options = [...suggestions, ...sameName];

  const merge = useMutation({
    mutationFn: async (keepId: string) => {
      const fn = isContact ? 'merge_contacts' : 'merge_organizations';
      unwrap(await supabase.rpc(fn, { keep_id: keepId, drop_id: self }));
    },
    onSuccess: onDone,
  });

  return (
    <div className="space-y-3 text-sm">
      <p className="text-slate-600">
        Merge <b>{selfName}</b> into another record. Everything attached (emails, phones, activity, deals, buildings) moves over, blanks are filled in, and the duplicate is removed.
      </p>
      {options.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-slate-600">Suggested</div>
          {options.map((o) => (
            <Button key={o.id} className="w-full justify-between" onClick={() => merge.mutate(o.id)} busy={merge.isPending}>
              Merge into {o.name}
            </Button>
          ))}
        </div>
      )}
      <div>
        <div className="mb-1 text-xs font-medium text-slate-600">Or choose any {isContact ? 'contact' : 'company'}</div>
        {isContact ? <ContactPicker onPick={(o) => o.id !== self && merge.mutate(o.id)} /> : <OrgPicker onPick={(o) => o.id !== self && merge.mutate(o.id)} />}
      </div>
      <ErrorBox error={merge.error} />
    </div>
  );
}

/** People you email often who aren't contacts yet (from Outlook sync). One click to add. */
function SuggestedContacts() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ['suggested-contacts'],
    queryFn: async () =>
      unwrap(
        await supabase
          .from('suggested_contacts')
          .select('id, email, name_guess, message_count, last_seen_at')
          .eq('status', 'pending')
          .gte('message_count', 2)
          .order('message_count', { ascending: false })
          .limit(50),
      ),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['suggested-contacts'] });
    qc.invalidateQueries({ queryKey: ['contacts'] });
  };
  const add = useMutation({
    mutationFn: async (s: (typeof data)[number]) => {
      const p = splitPersonName(s.name_guess ?? '');
      const fallback = s.email.split('@')[0]!;
      const contact = unwrap(
        await supabase
          .from('contacts')
          .insert({ first_name: p.first ?? fallback, last_name: p.last, suffix: p.suffix, display_name: s.name_guess, source: 'email' })
          .select('id')
          .single(),
      );
      unwrap(await supabase.from('contact_emails').insert({ contact_id: contact.id, email: s.email, is_primary: true }));
      unwrap(await supabase.from('suggested_contacts').update({ status: 'accepted', created_contact_id: contact.id }).eq('id', s.id));
    },
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: async (id: string) => unwrap(await supabase.from('suggested_contacts').update({ status: 'dismissed' }).eq('id', id)),
    onSuccess: refresh,
  });
  if (data.length === 0) return null;
  return (
    <Card title={`Suggested contacts (${data.length})`} className="mb-4">
      <p className="mb-2 text-sm text-slate-600">People you email who aren’t contacts yet. Adding one starts logging your emails with them from now on.</p>
      <ul className="divide-y divide-slate-100">
        {data.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <span className="font-medium text-slate-900">{s.name_guess || s.email}</span>
              {s.name_guess && <span className="ml-2 text-slate-500">{s.email}</span>}
              <div className="text-xs text-slate-500">
                {s.message_count} emails · last {relative(s.last_seen_at)}
              </div>
            </div>
            <Button size="sm" variant="primary" busy={add.isPending && add.variables?.id === s.id} onClick={() => add.mutate(s)}>
              Add contact
            </Button>
            <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(s.id)}>
              Not a contact
            </Button>
          </li>
        ))}
      </ul>
      <ErrorBox error={add.error ?? dismiss.error} />
    </Card>
  );
}
